import {useEffect, useRef, KeyboardEvent, TextareaHTMLAttributes, useState} from "react";
import styled from "styled-components";
import {Button} from "@/components/ui/Button.tsx";
import {IoChevronDown} from "react-icons/io5";

const StyledTextarea = styled.textarea`
    resize: none;
    overflow: hidden;
    width: 100%;
    border: none;
    outline: none;
    line-height: 25px;
`

interface TCustomTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    value: string;
    setValue: (value: string) => void;
    placeholder?: string;
    showExpand?: boolean
}

export const CustomTextarea = ({ value, setValue, placeholder, showExpand, ...props }: TCustomTextareaProps) => {
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showExpandButton, setShowExpandButton] = useState(false);

    const resizeTextArea = () => {
        if (!textAreaRef.current) return;
        textAreaRef.current.style.height = "auto";
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;

        // Check if content height exceeds 250px
        if (textAreaRef.current.scrollHeight > 250) {
            setShowExpandButton(true);
        } else {
            setShowExpandButton(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            event.currentTarget.blur();
        }
    };

    const toggleExpand = () => {
        if (!containerRef.current) return;
        setIsExpanded(prev => !prev);
        containerRef.current.style.maxHeight = isExpanded ? '250px' : '900px';
    };

    useEffect(() => {
        resizeTextArea();
        window.addEventListener("resize", resizeTextArea);
        return () => window.removeEventListener("resize", resizeTextArea);
    }, []);

    useEffect(() => {
        resizeTextArea();
    }, [value]);

    return (
        <div ref={containerRef}
             style={{
                 maxHeight: showExpand ? '250px' : '100%',
                 overflow: 'hidden',
                 width: '100%',
                 transition: 'max-height 0.3s ease',
                 position: 'relative',
        }}
        >
            <StyledTextarea
                className="textarea"
                value={value}
                ref={textAreaRef}
                placeholder={placeholder}
                rows={1}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                    setValue(e.target.value);
                    resizeTextArea();
                }}
                {...props}
            />
            {showExpand && showExpandButton && (
                <Button
                    variant="ghost"
                    style={{
                        position: 'absolute',
                        borderRadius: '50%',
                        bottom: '0',
                        right: '8px',
                        background: '#F6F3F3',
                        padding: '4px'
                    }}
                    onClick={toggleExpand}
                >
                    <IoChevronDown
                        size={18}
                        style={{
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                </Button>
            )}
        </div>
    );
}
